'use client';

import { trackingIds } from '@/constants/tracking-ids';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

// Define window interface extensions
declare global {
  interface Window {
    HerodashToken?: string;
    Herodash?: (...args: any[]) => void;
    HeroDashChatWidget?: {
      on: (
        event: string,
        callback: () => void,
      ) => {
        remoteReady: () => void;
      };
    };
  }
}

// Type definition for Herodash function
interface HerodashFunction {
  (...args: any[]): void;
  q?: any[];
}

const hiddenRoutes = ['/cart', '/checkout'];

/**
 * Custom hook to initialize and manage HeroChat widget
 * @param token - Authentication token for HeroChat
 */
function useHerodash(token: string) {
  const pathname = usePathname();

  const scrollListenerRef = useRef<((event: Event) => void) | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // Clean up scroll listener
  const removeScrollListener = () => {
    if (scrollListenerRef.current) {
      window.removeEventListener('scroll', scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
  };

  // Clean up observer
  const removeObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  // Effect for handling visibility based on route
  useEffect(() => {
    const shouldHideChat = hiddenRoutes.some(route => pathname.toLowerCase().includes(route));

    const chatIframe = document.getElementById('heroDash-chat-widget-iframe');

    if (chatIframe) {
      if (shouldHideChat) {
        chatIframe.style.display = 'none';
        removeScrollListener();
      } else {
        chatIframe.style.display = 'block';
      }
    }

    // Setup MutationObserver
    if (!observerRef.current) {
      observerRef.current = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const chatIframe = mutation.target as HTMLElement;
            const shouldHideChat = hiddenRoutes.some(route => pathname.toLowerCase().includes(route));

            if (shouldHideChat && chatIframe.style.display !== 'none') {
              chatIframe.style.display = 'none';
            }
          }
        });
      });

      if (chatIframe) {
        observerRef.current.observe(chatIframe, {
          attributes: true,
        });
      }
    }

    // Cleanup on route change
    return () => {
      removeScrollListener();
      removeObserver();
    };
  }, [pathname]);

  useEffect(() => {
    // Initialize Herodash if not already present
    if (!window.Herodash) {
      window.HerodashToken = token;

      // Create script element
      const script = document.createElement('script');

      script.async = true;
      script.src = `https://guest.herodash.ai/chat.js?token=${token}&timesMap=${Math.random()}`;

      // Initialize queue function
      window.Herodash = function () {
        ((window.Herodash as HerodashFunction).q = (window.Herodash as HerodashFunction).q || []).push(arguments);
      };

      // Append script to document head
      document.head.appendChild(script);
    }

    // Initialize widget when script loads
    const initializeWidget = () => {
      if (window.HeroDashChatWidget) {
        window.HeroDashChatWidget.on('load', () => {
          const chatIframe = document.getElementById('heroDash-chat-widget-iframe');

          if (chatIframe) {
            chatIframe.style.zIndex = '100';
            chatIframe.title = 'chat-widget';

            // Check if current route should hide chat
            const shouldHideChat = hiddenRoutes.some(route => pathname.toLowerCase().includes(route));

            if (shouldHideChat) {
              chatIframe.style.display = 'none';
            }

            // Setup MutationObserver if not already set
            if (!observerRef.current) {
              observerRef.current = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const chatIframe = mutation.target as HTMLElement;
                    const shouldHideChat = hiddenRoutes.some(route => pathname.toLowerCase().includes(route));

                    if (shouldHideChat && chatIframe.style.display !== 'none') {
                      chatIframe.style.display = 'none';
                    }
                  }
                });
              });

              observerRef.current.observe(chatIframe, {
                attributes: true,
              });
            }
          }
        }).remoteReady();
      }
    };

    // Handle script load event
    const handleScriptLoad = () => {
      initializeWidget();
    };

    // Add load event listener
    document.querySelector('script[src*="herodash.ai/chat.js"]')?.addEventListener('load', handleScriptLoad);

    // Cleanup
    return () => {
      removeScrollListener();
      removeObserver();
      document.querySelector('script[src*="herodash.ai/chat.js"]')?.removeEventListener('load', handleScriptLoad);
    };
  }, [pathname, token]);
}

// Example usage in a React component:
/*
import { useHeroChat } from './useHeroChat';

function ChatWidget({ token }: { token: string }) {
  useHeroChat(token);
  return null;
}

export default ChatWidget;
*/

export default function ChatWidget() {
  useHerodash(trackingIds.heroDashToken);

  return null;
}
